/**
 * RenameWidgetModal — single-field FormModal for the kebab menu's Rename
 * action. Submits via useUpdateWidget, preserving the widget's current grid
 * position (UpdateDashboardWidgetPayload requires all 4 grid fields).
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { toast } from 'sonner';
import { useUpdateWidget } from '@/api/dashboardConfig';
import { MUTATION_ERROR_TOAST_ID } from '@/lib/toastIds';
import { FormModal } from '@/components/FormModal';
import { labelStyle, fieldStyle, btnPrimaryStyle, btnGhostStyle, errorTextStyle } from '@/components/ProjectForm';
import type { DashboardWidgetResponse } from '@/types/domain';

export interface RenameWidgetModalProps {
  widget: DashboardWidgetResponse;
  onClose: () => void;
  onRenamed: () => void;
}

export function RenameWidgetModal({ widget, onClose, onRenamed }: RenameWidgetModalProps) {
  const [name, setName] = useState(widget.title ?? '');
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateWidget();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError('Give this widget a name.');
      return;
    }
    setError(null);
    update.mutate(
      {
        id: widget.id,
        dashboardId: widget.dashboardId,
        payload: { title: trimmed, gridX: widget.gridX, gridY: widget.gridY, gridW: widget.gridW, gridH: widget.gridH },
      },
      {
        onSuccess: () => { toast.success('Widget renamed.'); onRenamed(); },
        onError: () => toast.error("Couldn't rename the widget.", { id: MUTATION_ERROR_TOAST_ID }),
      },
    );
  }

  return (
    <FormModal title="Rename widget" onClose={onClose} width={360}>
      <form onSubmit={handleSubmit} noValidate>
        <div style={{ marginBottom: 14 }}>
          <label htmlFor="rename-widget-name" style={labelStyle}>Name</label>
          <input
            id="rename-widget-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="focus-ring"
            style={fieldStyle}
          />
        </div>
        {error && <span style={errorTextStyle}>{error}</span>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="pressable focus-ring" style={btnGhostStyle}>Cancel</button>
          <button type="submit" disabled={update.isPending} className="pressable focus-ring" style={{ ...btnPrimaryStyle, opacity: update.isPending ? 0.6 : 1 }}>
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </FormModal>
  );
}
